<template>
  <!--
    A ESPERA DA PESCARIA: o barquinho pesqueiro do pack da vila (12 quadros de balanço, vista
    lateral) numa vigia redonda — céu em cima, a linha d'água no meio, e a linha de pesca descendo
    até o anzol, que é o sprite que o jogo já usa. O boneco de CSS desenhado à mão saiu de vez
    (autor, 03/09: "ficou bem bugado"); a única coisa desenhada aqui é a linha de pesca — um fio de
    1px não é arte, é um fio. De tempos em tempos um peixinho pula perto do barco (9 quadros do
    mesmo pack): é a água dizendo que está viva.
  -->
  <div class="vigia" :class="{ 'vigia--puxando': isPulling }" aria-hidden="true">
    <div class="vigia__ceu" />
    <div class="vigia__mar" />

    <div class="vigia__peixe-pulo" />

    <div class="vigia__balanco">
      <div class="vigia__barco" />
    </div>

    <div class="vigia__linha" />
    <i class="fa-crosshairs vigia__anzol" />
  </div>
</template>

<script>
export default {
  name: 'FishingCharacter',
  props: {
    isPulling: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style scoped>
.vigia {
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid var(--madeira-escura);
  box-shadow: 0 6px 0 rgba(0, 0, 0, 0.35), inset 0 0 0 2px var(--latao);
}

/* O céu de fim de tarde e o mar: as cores dos tokens, nada desenhado. */
.vigia__ceu {
  position: absolute;
  inset: 0 0 45% 0;
  background: linear-gradient(to bottom, #cfe6ee, #9ec9d8);
}

.vigia__mar {
  position: absolute;
  inset: 55% 0 0 0;
  top: 45%;
  background: linear-gradient(to bottom, var(--agua-rasa, #2e6b8a), var(--agua-abissal));
}

/* O balanço: o barco sobe e desce devagar sobre a linha d'água, além dos quadros da folha. */
.vigia__balanco {
  position: absolute;
  left: 50%;
  top: 45%;
  transform: translate(-50%, -78%);
  animation: vigia-balancar 4.5s var(--curva-agua) infinite;
}

/* A folha: 704×384, grade 4×3, quadros de 176×128 — andados um a um com `step-end`. */
.vigia__barco {
  width: 176px;
  height: 128px;
  background: url('/images/ui/barco-espera.png') no-repeat 0 0;
  image-rendering: pixelated;
  animation: vigia-quadros 1.6s step-end infinite;
}

/* A linha de pesca: da proa até o anzol dentro d'água. */
.vigia__linha {
  position: absolute;
  left: 63%;
  top: 40%;
  width: 1px;
  height: 38%;
  background: rgba(240, 240, 235, 0.75);
  transform-origin: top;
  animation: vigia-linha 4.5s var(--curva-agua) infinite;
}

.vigia__anzol {
  position: absolute;
  left: 63%;
  top: 76%;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  background-size: contain;
  background-repeat: no-repeat;
  image-rendering: pixelated;
  animation: vigia-anzol 4.5s var(--curva-agua) infinite;
}

/* O peixinho que pula (288×32, 9 quadros de 32×32): pula uma vez a cada ciclo e some. */
.vigia__peixe-pulo {
  position: absolute;
  left: 18%;
  top: 38%;
  width: 32px;
  height: 32px;
  background: url('/images/ui/peixe-pulo.png') no-repeat 0 0;
  image-rendering: pixelated;
  transform: scale(2);
  transform-origin: bottom center;
  animation: vigia-pulo 7s step-end infinite;
}

/* Puxando: tudo balança mais rápido — o mesmo movimento, com pressa. */
.vigia--puxando .vigia__balanco,
.vigia--puxando .vigia__linha,
.vigia--puxando .vigia__anzol {
  animation-duration: 1.2s;
}

@keyframes vigia-balancar {
  0%, 100% { transform: translate(-50%, -78%) rotate(-1.5deg); }
  50% { transform: translate(-50%, -74%) rotate(1.5deg); }
}

/* Os 12 quadros da folha, linha a linha (4 colunas × 3 linhas). */
@keyframes vigia-quadros {
  0%       { background-position: 0 0; }
  8.33%    { background-position: -176px 0; }
  16.66%   { background-position: -352px 0; }
  25%      { background-position: -528px 0; }
  33.33%   { background-position: 0 -128px; }
  41.66%   { background-position: -176px -128px; }
  50%      { background-position: -352px -128px; }
  58.33%   { background-position: -528px -128px; }
  66.66%   { background-position: 0 -256px; }
  75%      { background-position: -176px -256px; }
  83.33%   { background-position: -352px -256px; }
  91.66%   { background-position: -528px -256px; }
}

@keyframes vigia-linha {
  0%, 100% { transform: rotate(-2deg) scaleY(1); }
  50% { transform: rotate(2deg) scaleY(1.05); }
}

@keyframes vigia-anzol {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(5px); }
}

/* O pulo: 9 quadros rápidos no comecinho do ciclo, o resto do tempo invisível. */
@keyframes vigia-pulo {
  0%     { background-position: 0 0; }
  1.5%   { background-position: -32px 0; }
  3%     { background-position: -64px 0; }
  4.5%   { background-position: -96px 0; }
  6%     { background-position: -128px 0; }
  7.5%   { background-position: -160px 0; }
  9%     { background-position: -192px 0; }
  10.5%  { background-position: -224px 0; }
  12%    { background-position: -256px 0; }
  13.5%, 100% { background-position: -288px 0; } /* fora da folha = transparente */
}

@media (prefers-reduced-motion: reduce) {
  .vigia__balanco,
  .vigia__barco,
  .vigia__linha,
  .vigia__anzol,
  .vigia__peixe-pulo {
    animation: none;
  }
}
</style>
